import type { ComponentProps, ReactNode, Ref } from 'react';
import type { LinkProps } from 'react-router';
import type { HeaderCellDropFn } from '../DraggableHeaderCell/DraggableHeaderCell';

import { useDrop } from 'react-dnd';
import { Link } from 'react-router';
import { Table } from 'rsuite';

import { deepReadKeyValue, getTestProps } from '../../../lib/helpers';

const { Cell } = Table;

const cellClasses =
  'px-4 py-2 h-full w-full flex items-center truncate group select-all ';

export type DataGridGetRowLinkOptionsFn = (
  accessor: string,
  rowData: any,
) => LinkProps | undefined;

interface DroppableCellProps extends Omit<
  ComponentProps<typeof Cell>,
  'children' | 'onDrop' | 'renderCell'
> {
  onDrop?: HeaderCellDropFn;
  accessor: string;
  notDraggable?: boolean;
  renderCell: (value: any, rowData: any) => ReactNode;
  getRowLinkOptions?: DataGridGetRowLinkOptionsFn;
  testId?: string;
}

const DroppableCell = ({
  onDrop,
  accessor,
  notDraggable,
  renderCell,
  getRowLinkOptions,
  testId,
  ...rest
}: DroppableCellProps) => {
  const rowData = rest.rowData || {};
  const [, drop] = useDrop<{ accessor: string }>({
    accept: 'column',
    canDrop: () => !notDraggable && !!onDrop,
    hover: (item, monitor) => {
      if (monitor.canDrop() && !monitor.didDrop() && !monitor.isOver()) {
        onDrop?.(item.accessor, accessor);
      }
    },
  });

  const linkOptions = getRowLinkOptions?.(accessor, rowData);

  const content = renderCell(deepReadKeyValue(accessor, rowData), rowData);

  if (onDrop) {
    return (
      <Cell ref={drop as unknown as Ref<HTMLDivElement>} {...rest}>
        <div
          className={cellClasses}
          {...getTestProps(testId, `droppable-row-${accessor}`)}
        >
          {content}
        </div>
      </Cell>
    );
  }

  if (linkOptions) {
    return (
      <Cell {...rest}>
        <Link
          className={
            cellClasses +
            'focus:no-underline hover:no-underline active:no-underline ' +
            'focus:text-slate-900 active:text-slate-900 hover:text-slate-900 ' +
            'dark:hover:text-slate-200 dark:active:text-slate-200'
          }
          {...linkOptions}
        >
          {content}
        </Link>
      </Cell>
    );
  }

  return (
    <Cell {...rest}>
      <div className={cellClasses}>{content}</div>
    </Cell>
  );
};

export default DroppableCell;
